import React, { useCallback } from 'react'
import { Button } from '@blueprintjs/core'
import { useApi } from '../../contexts/ApiProvider'
import { downloadFile } from '../../utils/downloadFile'
import { useSurvey } from '../../contexts/SurveyProvider'
import { useWindow } from '../../contexts/WindowProvider'
import { AppToaster } from '../toaster'

/**
 * SegmentationScoresExportToExcel handles the export functionality for segmentation scores.
 * @param {Object} props - Component props
 * @param {Array} props.segmentationsScores - Array of segmentation scores to export
 * @param {boolean} props.disabled - Whether the export button should be disabled
 * @returns {JSX.Element} The export button component
 */
export function SegmentationScoresExportToExcelButton({
  segmentationsScores,
  disabled = false,
}) {
  const { surveyId } = useSurvey()
  const api = useApi()
  const { currentSurvey } = useWindow()

  const exportToExcel = useCallback(async () => {
    const exportFileName = 'Segmentation Scores Table'
    const surveyName = currentSurvey.name

    const response = await api.post(
      `/survey/${surveyId}/segmentation_scores_table/export_to_excel`,
      { score_data: segmentationsScores },
      { responseType: 'blob' }
    )

    if (response.ok) {
      const data = response.body
      downloadFile(data, `${exportFileName} - ${surveyName} - .xlsx`)
    } else {
      const message = `Error exporting - code: ${response.status} - ${response?.body?.messages?.json?.['Segmentation scores']}`
      console.error(message)
      AppToaster.show({
        message,
        intent: 'danger',
        icon: 'error',
      })
    }
  }, [api, surveyId, segmentationsScores, currentSurvey.name])

  return (
    <Button
      style={{ borderRadius: 4 }}
      intent="primary"
      icon="export"
      onClick={exportToExcel}
      text="Export to Excel"
      disabled={disabled}
    />
  )
}
